<template>
  <span class="dropzone-wrapper">
      <div
              class="dropzone-previews"
              :id="containerId + '-above'"
              v-show="layout === 'stacked'"
      />
  <vue-dropzone
          :ref="componentRef"
          class="dropzone-component"
          :options="mergedOptions"
          :id="elementId"
          :include-styling="false"
          :use-custom-slot="true"
          @vdropzone-thumbnail="thumbnail"
          @vdropzone-success="successEvent"
          @vdropzone-error="errorEvent"
          @vdropzone-file-added="fileAddedEvent"
          @vdropzone-removed-file="removeEvent"
          @vdropzone-queue-complete="queueCompleteEvent"
          @vdropzone-max-files-exceeded="maxFilesExceededEvent"
          @vdropzone-upload-progress="progressEvent"
          v-if="templateLoaded"
          :style="dropzoneStyles"
  >
    <div class="dropzone-custom-content">
      <template v-if="showDefaultText">
      <div class="dropzone-custom-title">
        Drag & Drop
      </div>
      <div class="subtitle">
        your files here, or <span class="link">browse</span>
      </div>
        </template>
      <template v-if="showAdditionalText">
      <div class="dropzone-custom-title">
        <i class="fas fa-plus additional-text-icon" />
        Add Another
      </div>
      </template>
    </div>
  </vue-dropzone>

      <div
              class="dropzone-previews"
              :id="containerId + '-below'"
              v-show="layout !== 'stacked'"
      />
  </span>
</template>

<script>
    import Vue from 'vue';
    import vue2Dropzone from 'vue2-dropzone';
    import DropzoneTemplate from './DropzoneTemplate';
    import 'vue2-dropzone/dist/vue2Dropzone.min.css';

    export default {
        name: 'Dropzone',
        components: {
            vueDropzone: vue2Dropzone,
        },
        props: {
            layout: {
                type: String,
                required: false,
                default: 'default',
                validator: (value) => {
                    return ['default', 'single', 'stacked'].indexOf(value) !== -1;
                }
            },
            componentHeight: {
                type: String,
                required: false,
                default: '8.125rem'
            },
            options: {
                type: Object,
                required: false,
                default() {
                    return {};
                }
            },
            componentRef: {
                type: String,
                required: true
            },
            elementId: {
                type: String,
                required: true
            },
            containerId: {
                type: String,
                required: true
            },

        },
        data: () => (
            {
                files: {},
                templateLoaded: false,
                previewHtml: null,
                fileIcons: {
                    pdf: 'pdf.svg',
                    ppt: 'powerpoint.svg',
                    pptx: 'powerpoint.svg',
                    doc: 'word.svg',
                    docx: 'word.svg',
                    xls: 'excel.svg',
                    xlsx: 'excel.svg',
                    zip: 'zipped.svg',
                    zipx: 'zipped.svg',
                    rar: 'zipped.svg',
                    '7z': 'zipped.svg'
                }
            }
        ),
        computed: {
            dropzoneStyles() {
                return {
                    height: this.calculateHeight,
                    display: this.layout === 'single' && Object.keys(this.files).length ? 'none' : 'flex'
                };
            },
            showDefaultText() {
                return !(
                    (
                        this.layout === 'single' && !!Object.keys(this.files).length
                    ) ||
                    (
                        this.layout === 'stacked' && Object.keys(this.files).length
                    )
                );
            },
            showAdditionalText() {
                return !!(this.layout === 'stacked' && Object.keys(this.files).length);
            },
            calculateHeight() {
                if (this.layout === 'stacked' && Object.keys(this.files).length) {
                    return '3.125rem';
                }
                return this.componentHeight;
            },
            mergedOptions() {
                let baseOptions = {
                    thumbnailWidth: 150,
                    previewTemplate: this.previewHtml,
                    autoProcessQueue: true,
                    previewsContainer: this.layout === 'stacked' ?
                        '#' + this.containerId + '-above' : '#' + this.containerId + '-below',
                    maxFiles: this.layout === 'single' ? 1 : 100
                };
                return {...baseOptions, ...this.options};
            }
        },
        created() {
            const ComponentClass = Vue.extend(DropzoneTemplate);
            const instance = new ComponentClass();
            instance.$mount();
            this.previewHtml = instance.$el.outerHTML;

            this.templateLoaded = true;
        },
        methods: {
            fileAddedEvent(file) {
                let fileIcon;
                let fileExtension = this.fileIcons[file.name.split('.').pop()];
                if (fileExtension) {
                    fileIcon = `${require('./../../static/file-icons/' + fileExtension)}`;
                }
                if (file.previewElement && fileIcon) {
                    this.thumbnail(file, fileIcon);
                }
            },
            successEvent(file, response) {
                this.$set(this.files, file.upload.uuid, response);
                this.$emit('ui-dropzone-success', file, response);
            },
            removeEvent(file) {
                if (this.files[file.upload.uuid]) {
                    this.$emit('ui-dropzone-remove', file, this.files[file.upload.uuid]);
                    this.$delete(this.files, file.upload.uuid);
                }
            },
            queueCompleteEvent() {
                this.$emit('ui-dropzone-queue-complete');
            },
            maxFilesExceededEvent(file) {
                this.$refs[this.componentRef].removeFile(file);
                this.$emit('ui-dropzone-max-files-exceeded');
            },
            thumbnail(file, dataUrl) {
                let j, len, ref, thumbnailElement;
                if (file.previewElement) {
                    file.previewElement.classList.remove('dz-file-preview');
                    ref = file.previewElement.querySelectorAll('[data-dz-thumbnail-bg]');
                    for (j = 0, len = ref.length; j < len; j++) {
                        thumbnailElement = ref[j];
                        thumbnailElement.alt = file.name;
                        thumbnailElement.style.backgroundImage = 'url("' + dataUrl + '")';
                    }
                    return setTimeout(((function (_this) {
                        return function () {
                            return file.previewElement.classList.add('dz-image-preview');
                        };
                    })(this)), 1);
                }
            },
            progressEvent(file, progress, bytesSent) {
                file.previewElement.querySelector('.progress-bar').style.width = progress + '%';
            },
            errorEvent(file, message, xhr) {
                file.previewElement.querySelector('.error-upload').innerHTML = 'Upload Failed!';
            }
        }
    };
</script>

<style lang="scss">
    @import "../../scss/foundation";

    .dropzone-wrapper {
        clear:   both;
        display: block;

        .dropzone-component {
            position:         relative;
            letter-spacing:   0.0125rem;
            color:            #777;
            transition:       background-color .2s linear;
            height:           8.125rem;
            text-align:       center;
            box-sizing:       border-box;
            border:           0.0625rem dashed rgba(173, 177, 187, 0.7);
            border-radius:    0.25rem;
            background-color: $gray-100;
            display:          flex;
            justify-content:  center;
            align-items:      center;

            &.dz-started {
                .dz-message {
                    display: none;
                }
            }

            .dropzone-custom-content {

                .dropzone-custom-title {
                    color:          $black;
                    font-family:    $headings-font-family;
                    font-size:      1rem;
                    letter-spacing: 0.01875rem;
                    line-height:    1.5rem;
                    font-weight:    700;

                    .additional-text-icon {
                        font-size: 0.875rem;
                        margin:    5px;
                        color:     $black;
                    }
                }

                .subtitle {
                    color:       $black;
                    font-family: $font-family-base;
                    font-size:   0.875rem;
                    line-height: 1.25rem;

                    .link {
                        color: $blue;

                        &:hover {
                            cursor:          pointer;
                            text-decoration: underline;
                        }
                    }
                }
            }
        }

    }

    .dropzone-previews {
        .dz-preview {
            box-sizing:       border-box;
            height:           4.375rem;
            border:           1px solid $gray-400;
            border-radius:    2px;
            background-color: $gray-100;
            margin-left:      0;
            margin-right:     0;
            margin-top:       10px;
            margin-bottom:    10px;
            padding:          15px;

            &.dz-processing .dz-progress {
                opacity:            1;
                -webkit-transition: all 0.2s linear;
                -moz-transition:    all 0.2s linear;
                -ms-transition:     all 0.2s linear;
                -o-transition:      all 0.2s linear;
                transition:         all 0.2s linear;
                border-radius:      1px;
                width:              100%;
                height:             4px;
                background-color:   #e0e0e2;

                .progress-bar {
                    border-radius:    1px;
                    height:           4px;
                    background-color: $link-color;
                }
            }

            &.dz-complete .dz-progress {
                opacity: 0;
                display: none;
            }

            &:not(.dz-processing) .dz-progress {
                -webkit-animation: pulse 6s ease infinite;
                -moz-animation:    pulse 6s ease infinite;
                -ms-animation:     pulse 6s ease infinite;
                -o-animation:      pulse 6s ease infinite;
                animation:         pulse 6s ease infinite;
            }

            .dz-progress {
                opacity:          1;
                z-index:          2;
                pointer-events:   none;
                border-radius:    1px;
                width:            100%;
                height:           4px;
                background-color: #e0e0e2;

                .dz-upload {
                    -webkit-transition: width 300ms ease-in-out;
                    -moz-transition:    width 300ms ease-in-out;
                    -ms-transition:     width 300ms ease-in-out;
                    -o-transition:      width 300ms ease-in-out;
                    transition:         width 300ms ease-in-out;
                    border-radius:      1px;
                    width:              100%;
                    height:             4px;
                    background-color:   #e0e0e2;
                }
            }

            .dz-image {
                margin-right: 15px;

                .image-thumbnail {
                    width:           1.5rem;
                    height:          1.5rem;
                    border-radius:   4px;
                    background-size: cover;
                }
            }

            .dz-details {
                overflow: hidden;

                .dz-filename {
                    color:          $gray-800;
                    font-family:    $headings-font-family;
                    font-size:      1rem;
                    font-weight:    800;
                    letter-spacing: 0.3px;
                    white-space:    nowrap;
                    overflow:       hidden;
                    text-overflow:  ellipsis;
                }

            }

            .dz-remove {
                color:       $gray-700;
                font-size:   1.3rem;
                margin-left: 15px;
                cursor:      pointer;
            }

            .error-upload {
                color: $danger;
            }
        }
    }
</style>
